import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../context/AuthContext';
import TicketChat from '../components/TicketChat';
import ContextualHelp from '../components/ContextualHelp';
import styles from './MedicalSOS.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface MedicalTicket {
  id: number;
  userId: number;
  username: string;
  inGameUsername: string;
  currentSystem: string;
  currentLocation: string;
  deathTimerRemaining: number;
  hostileEnvironment?: boolean;
  pvpRiskArea?: boolean;
  status: string;
  assignedMedicId?: number;
  assignedMedicName?: string;
  createdAt: string;
  updatedAt?: string;
  claimedAt?: string;
  completedAt?: string;
  estimatedArrivalTime?: string;
  medicNotes?: string;
  notes?: string;
}

type TabType = 'request' | 'available' | 'assignments' | 'myTickets' | 'manageAll' | 'reports';

const MedicalSOS: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Helper function to calculate actual remaining time
  const calculateActualRemainingTime = (ticket: MedicalTicket): { minutes: number; isExpired: boolean; display: string } => {
    const createdAt = new Date(ticket.createdAt);
    const now = new Date();
    const elapsedMinutes = Math.floor((now.getTime() - createdAt.getTime()) / 60000);
    const remainingMinutes = ticket.deathTimerRemaining - elapsedMinutes;
    
    if (remainingMinutes <= 0) {
      return {
        minutes: 0,
        isExpired: true,
        display: 'EXPIRED'
      };
    }
    
    return {
      minutes: remainingMinutes,
      isExpired: false,
      display: `${remainingMinutes} min`
    };
  };

  // Request SOS state
  const [myTicket, setMyTicket] = useState<MedicalTicket | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [inGameUsername, setInGameUsername] = useState('');
  const [currentSystem, setCurrentSystem] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [deathTimer, setDeathTimer] = useState('');
  const [hostileEnvironment, setHostileEnvironment] = useState(false);
  const [pvpRiskArea, setPvpRiskArea] = useState(false);
  const [notes, setNotes] = useState('');

  // Available tickets state
  const [availableTickets, setAvailableTickets] = useState<MedicalTicket[]>([]);
  const [availableTicketsCount, setAvailableTicketsCount] = useState<number>(0);

  // My assignments state
  const [myAssignments, setMyAssignments] = useState<MedicalTicket[]>([]);
  const [myAssignmentsCount, setMyAssignmentsCount] = useState<number>(0);
  const [updateFormData, setUpdateFormData] = useState<{ [key: number]: { eta: string; notes: string } }>({});

  // My tickets state
  const [myTicketHistory, setMyTicketHistory] = useState<MedicalTicket[]>([]);
  const [expandedMyTicket, setExpandedMyTicket] = useState<number | null>(null);
  const [myTicketFilter, setMyTicketFilter] = useState<string>('all');

  // Manage all tickets state (medics/admins only)
  const [allTicketHistory, setAllTicketHistory] = useState<MedicalTicket[]>([]);
  const [expandedAllTicket, setExpandedAllTicket] = useState<number | null>(null);
  const [allTicketFilter, setAllTicketFilter] = useState<string>('all');

  // Reports state (medics/admins only)
  const [reportData, setReportData] = useState<any>(null);
  const [reportStartDate, setReportStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Chat state
  const [activeChatTicketId, setActiveChatTicketId] = useState<number | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<{ [ticketId: number]: number }>({});

  // Check if user has medic permissions
  const isMedic = user?.positions?.some((pos: any) => pos.canManageMedicalTickets) || user?.role === 'Admin' || false;

  // Fetch unread message count for a ticket
  const fetchUnreadCount = async (ticketId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${ticketId}/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const count = await response.json();
        setUnreadCounts(prev => ({ ...prev, [ticketId]: count }));
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  // Fetch unread counts for multiple tickets
  const fetchUnreadCounts = async (tickets: MedicalTicket[]) => {
    for (const ticket of tickets) {
      await fetchUnreadCount(ticket.id);
    }
  };

  // Refresh user permissions on component mount to prevent stale cache issues
  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    if (isMedic) {
      fetchMyAssignmentsCount();
      fetchAvailableTicketsCount();
    }
  }, [isMedic]);

  useEffect(() => {
    if (activeTab === 'request') {
      checkForMyTicket();
      
      // Auto-refresh every 10 seconds when on request tab with active ticket
      const interval = setInterval(() => {
        checkForMyTicket();
      }, 10000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'available') {
      fetchAvailableTickets();
      
      // Auto-refresh available tickets and counter every 15 seconds
      const interval = setInterval(() => {
        fetchAvailableTickets();
        fetchAvailableTicketsCount();
      }, 15000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'assignments') {
      fetchMyAssignments();
      
      // Auto-refresh assignments, counter, and unread counts every 15 seconds
      const interval = setInterval(() => {
        fetchMyAssignments();
        fetchMyAssignmentsCount();
      }, 15000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'myTickets') {
      fetchMyTicketHistory();
      
      // Auto-refresh ticket history every 15 seconds
      const interval = setInterval(() => {
        fetchMyTicketHistory(false);
      }, 15000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'manageAll') {
      fetchAllTicketHistory();
    } else if (activeTab === 'reports') {
      fetchReportData();
    }
  }, [activeTab]);

  // === Request SOS Functions ===
  const checkForMyTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/my-ticket`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyTicket(data);
        if (data) {
          fetchUnreadCount(data.id);
        }
      } else if (response.status === 404) {
        setMyTicket(null);
      }
    } catch (err) {
      console.error('Error checking for active ticket:', err);
    }
  };

  const handleSubmitSOS = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const captchaToken = captchaRef.current?.getValue();
    if (!captchaToken) {
      setError('Please complete the CAPTCHA');
      return;
    }

    const deathTimerNum = parseInt(deathTimer);
    if (isNaN(deathTimerNum) || deathTimerNum < 1) {
      setError('Please enter a valid death timer');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          inGameUsername,
          currentSystem,
          currentLocation,
          deathTimerRemaining: deathTimerNum,
          hostileEnvironment,
          pvpRiskArea,
          notes,
          captchaToken
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('SOS request submitted successfully!');
        setMyTicket(data);
        // Reset form
        setInGameUsername('');
        setCurrentSystem('');
        setCurrentLocation('');
        setDeathTimer('');
        setHostileEnvironment(false);
        setPvpRiskArea(false);
        setNotes('');
        captchaRef.current?.reset();
        
        // Switch to My Tickets tab and refresh the history
        setTimeout(() => {
          setActiveTab('myTickets');
        }, 1500); // Give user time to see success message
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit SOS request');
      }
    } catch (err) {
      setError('An error occurred while submitting your request');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelMyTicket = async () => {
    if (!myTicket || !window.confirm('Are you sure you want to cancel your SOS request?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${myTicket.id}/cancel`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Ticket cancelled successfully');
        setMyTicket(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to cancel ticket');
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
    }
  };

  // === Available Tickets Functions ===
  const fetchAvailableTicketsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTicketsCount(data.length);
      }
    } catch (err) {
      console.error('Failed to fetch available tickets count:', err);
    }
  };

  const fetchAvailableTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTickets(data);
        setAvailableTicketsCount(data.length);
      }
    } catch (err) {
      console.error('Error fetching available tickets:', err);
    }
  };

  const handleClaimTicket = async (ticketId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${ticketId}/claim`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Ticket claimed successfully!');
        fetchAvailableTickets();
        // Switch to assignments tab
        setActiveTab('assignments');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to claim ticket');
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
    }
  };

  // === My Assignments Functions ===
  const fetchMyAssignmentsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/my-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyAssignmentsCount(data.length);
      }
    } catch (err) {
      console.error('Failed to fetch assignments count:', err);
    }
  };

  const fetchMyAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/my-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyAssignments(data);
        setMyAssignmentsCount(data.length);
        fetchUnreadCounts(data);
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  };

  const handleUpdateTicket = async (ticketId: number) => {
    const formData = updateFormData[ticketId];
    if (!formData) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${ticketId}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          estimatedArrivalTime: formData.eta,
          medicNotes: formData.notes
        })
      });

      if (response.ok) {
        setSuccess('Ticket updated successfully!');
        fetchMyAssignments();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update ticket');
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
    }
  };

  const handleCompleteTicket = async (ticketId: number) => {
    if (!window.confirm('Mark this ticket as complete?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${ticketId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Ticket completed successfully!');
        fetchMyAssignments();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to complete ticket');
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
    }
  };

  const handleReleaseTicket = async (ticketId: number) => {
    if (!window.confirm('Release this ticket back to available pool?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/${ticketId}/release`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Ticket released successfully');
        fetchMyAssignments();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to release ticket');
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
    }
  };

  // === My Tickets History Functions ===
  const fetchMyTicketHistory = async (showLoading: boolean = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/my-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyTicketHistory(data);
      }
    } catch (err) {
      console.error('Error fetching ticket history:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // === Manage All Tickets Functions (Medics/Admins) ===
  const fetchAllTicketHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/MedicalTickets/all-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAllTicketHistory(data);
      } else if (response.status === 403) {
        setError('You do not have permission to view all tickets');
      }
    } catch (err) {
      console.error('Error fetching all ticket history:', err);
      setError('Failed to load ticket history');
    } finally {
      setLoading(false);
    }
  };

  // === Reports Functions ===
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        startDate: new Date(reportStartDate).toISOString(),
        endDate: new Date(reportEndDate).toISOString()
      });
      const response = await fetch(`${API_URL}/api/MedicalTickets/reports?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        setError('Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return styles.statusPending;
      case 'inprogress':
        return styles.statusInProgress;
      case 'completed':
        return styles.statusCompleted;
      case 'cancelled':
        return styles.statusCancelled;
      default:
        return '';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h1 className={styles.title}>Medical SOS</h1>
          <ContextualHelp featureArea="medical-sos" />
        </div>
        <p className={styles.subtitle}>Emergency Medical Assistance for Star Citizen Operations</p>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        {/* Tab Navigation */}
        <div className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${activeTab === 'request' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('request')}
        >
          Request SOS
        </button>
        {isMedic && (
          <>
            <button
              className={`${styles.tabButton} ${activeTab === 'available' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('available')}
            >
              Pending Tickets (<span style={{ fontWeight: availableTicketsCount > 0 ? 'bold' : 'normal', color: availableTicketsCount > 0 ? '#ff4444' : 'inherit' }}>{availableTicketsCount}</span>)
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'assignments' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('assignments')}
            >
              My Assignments (<span style={{ fontWeight: myAssignmentsCount > 0 ? 'bold' : 'normal', color: myAssignmentsCount > 0 ? '#ff4444' : 'inherit' }}>{myAssignmentsCount}</span>)
            </button>
          </>
        )}
        <button
          className={`${styles.tabButton} ${activeTab === 'myTickets' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('myTickets')}
        >
          My Requests
        </button>
        {isMedic && (
          <>
            {(user?.flags?.includes('AccessMedicalReports') || user?.role === 'Admin') && (
              <button
                className={`${styles.tabButton} ${activeTab === 'manageAll' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('manageAll')}
              >
                Manage All Tickets
              </button>
            )}
            {(user?.flags?.includes('AccessMedicalReports') || user?.role === 'Admin') && (
              <button
                className={`${styles.tabButton} ${activeTab === 'reports' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('reports')}
              >
                Reports
              </button>
            )}
          </>
        )}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* REQUEST SOS TAB */}
        {activeTab === 'request' && (
          <div className={styles.tabPanel}>
            {loading ? (
              <p>Loading...</p>
            ) : myTicket ? (
              <div className={styles.activeTicketCard}>
                <h2>Your Active SOS Request</h2>
                <div className={styles.ticketDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Status:</span>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(myTicket.status)}`}>
                      {myTicket.status}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>In-Game Username:</span>
                    <span>{myTicket.inGameUsername}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>System:</span>
                    <span>{myTicket.currentSystem}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Location:</span>
                    <span>{myTicket.currentLocation}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Death Timer:</span>
                    <span>
                      {myTicket.deathTimerRemaining} minutes (Original) 
                      <br />
                      <span style={{ 
                        color: calculateActualRemainingTime(myTicket).isExpired ? '#ef4444' : '#10b981',
                        fontWeight: 'bold' 
                      }}>
                        {calculateActualRemainingTime(myTicket).display} (Remaining)
                      </span>
                    </span>
                  </div>
                  {myTicket.assignedMedicName && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Assigned Medic:</span>
                      <span className={styles.medicName}>{myTicket.assignedMedicName}</span>
                    </div>
                  )}
                  {myTicket.estimatedArrivalTime && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>ETA:</span>
                      <span>{myTicket.estimatedArrivalTime}</span>
                    </div>
                  )}
                  {myTicket.medicNotes && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Medic Status:</span>
                      <span>{myTicket.medicNotes}</span>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Created:</span>
                    <span>{formatDateTime(myTicket.createdAt)}</span>
                  </div>
                  {myTicket.claimedAt && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Claimed:</span>
                      <span>{formatDateTime(myTicket.claimedAt)}</span>
                    </div>
                  )}
                </div>
                <div className={styles.ticketActions}>
                  <button className="button" onClick={checkForMyTicket}>
                    Refresh Status
                  </button>
                  {myTicket.status === 'InProgress' && (
                    <button 
                      className="button"
                      onClick={() => setActiveChatTicketId(myTicket.id)}
                      title="Chat with your assigned medic"
                    >
                      💬 Chat with Medic {unreadCounts[myTicket.id] > 0 && (
                        <span style={{ fontWeight: 'bold', color: '#ff4444', marginLeft: '5px' }}>
                          ({unreadCounts[myTicket.id]})
                        </span>
                      )}
                    </button>
                  )}
                  {myTicket.status === 'Pending' && (
                    <button className="cancelButton" onClick={handleCancelMyTicket}>
                      Cancel Request
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitSOS} className={styles.sosForm}>
                <h2>Submit Medical SOS Request</h2>
                <div className={styles.formGroup}>
                  <label htmlFor="inGameUsername">In-Game Username *</label>
                  <input
                    id="inGameUsername"
                    type="text"
                    value={inGameUsername}
                    onChange={(e) => setInGameUsername(e.target.value)}
                    required
                    maxLength={100}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="currentSystem">Current System *</label>
                  <input
                    id="currentSystem"
                    type="text"
                    value={currentSystem}
                    onChange={(e) => setCurrentSystem(e.target.value)}
                    placeholder="e.g., Stanton, Pyro"
                    required
                    maxLength={100}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="currentLocation">Current Location *</label>
                  <input
                    id="currentLocation"
                    type="text"
                    value={currentLocation}
                    onChange={(e) => setCurrentLocation(e.target.value)}
                    placeholder="e.g., Crusader, Port Olisar"
                    required
                    maxLength={200}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="deathTimer">Death Timer (minutes) *</label>
                  <input
                    id="deathTimer"
                    type="number"
                    value={deathTimer}
                    onChange={(e) => setDeathTimer(e.target.value)}
                    min="1"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="hostileEnvironment" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      id="hostileEnvironment"
                      type="checkbox"
                      checked={hostileEnvironment}
                      onChange={(e) => setHostileEnvironment(e.target.checked)}
                      style={{ width: 'auto', cursor: 'pointer' }}
                    />
                    <span>Hostile Environment</span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="pvpRiskArea" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      id="pvpRiskArea"
                      type="checkbox"
                      checked={pvpRiskArea}
                      onChange={(e) => setPvpRiskArea(e.target.checked)}
                      style={{ width: 'auto', cursor: 'pointer' }}
                    />
                    <span>PvP Risk Area</span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="notes">Additional Notes</label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional information that might help the medic find you"
                    maxLength={500}
                    rows={3}
                  />
                </div>
                <div className={styles.captchaContainer}>
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                  />
                </div>
                <button type="submit" className="button danger" disabled={submitting}>
                  {submitting ? '⏳ Submitting...' : '🚨 Submit SOS Ticket'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* AVAILABLE TICKETS TAB */}
        {activeTab === 'available' && isMedic && (
          <div className={styles.tabPanel}>
            <h2>Pending Tickets</h2>
            <p className={styles.tabDescription}>Unclaimed tickets from other players needing medical assistance</p>
            {loading ? (
              <p>Loading...</p>
            ) : availableTickets.length === 0 ? (
              <p className={styles.emptyMessage}>No pending SOS requests at this time.</p>
            ) : (
              <div className={styles.ticketsList}>
                {availableTickets.map(ticket => (
                  <div key={ticket.id} className={styles.ticketCard}>
                    <div className={styles.ticketHeader}>
                      <span className={styles.ticketId}>Ticket #{ticket.id}</span>
                      <span className={`${styles.statusBadge} ${getStatusBadgeClass(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <div className={styles.ticketBody}>
                      <p><strong>Player:</strong> {ticket.inGameUsername}</p>
                      <p><strong>System:</strong> {ticket.currentSystem}</p>
                      <p><strong>Location:</strong> {ticket.currentLocation}</p>
                      <p>
                        <strong>Death Timer:</strong> {ticket.deathTimerRemaining} min (Original) | 
                        <span style={{ 
                          color: calculateActualRemainingTime(ticket).isExpired ? '#ef4444' : '#10b981',
                          fontWeight: 'bold' 
                        }}>
                          {calculateActualRemainingTime(ticket).display} (Remaining)
                        </span>
                      </p>
                      <p><strong>Created:</strong> {formatDateTime(ticket.createdAt)}</p>
                      {ticket.notes && <p><strong>Notes:</strong> {ticket.notes}</p>}
                    </div>
                    <div className={styles.ticketActions}>
                      {ticket.userId === user?.id ? (
                        <button 
                          className="button"
                          disabled
                          title="You cannot claim your own ticket"
                        >
                          Your Own Ticket
                        </button>
                      ) : (
                        <button 
                          className="button"
                          onClick={() => handleClaimTicket(ticket.id)}
                        >
                          Claim Ticket
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MY ASSIGNMENTS TAB */}
        {activeTab === 'assignments' && isMedic && (
          <div className={styles.tabPanel}>
            <h2>My Assignments</h2>
            <p className={styles.tabDescription}>Tickets you are currently treating as a medic</p>
            {loading ? (
              <p>Loading...</p>
            ) : myAssignments.length === 0 ? (
              <p className={styles.emptyMessage}>You have no active assignments.</p>
            ) : (
              <div className={styles.assignmentsList}>
                {myAssignments.map(ticket => (
                  <div key={ticket.id} className={styles.assignmentCard}>
                    <div className={styles.ticketHeader}>
                      <span className={styles.ticketId}>Ticket #{ticket.id}</span>
                      <span className={`${styles.statusBadge} ${getStatusBadgeClass(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <div className={styles.ticketBody}>
                      <p><strong>Player:</strong> {ticket.inGameUsername}</p>
                      <p><strong>System:</strong> {ticket.currentSystem}</p>
                      <p><strong>Location:</strong> {ticket.currentLocation}</p>
                      <p><strong>Death Timer:</strong> {ticket.deathTimerRemaining} minutes</p>
                      <p><strong>Claimed:</strong> {formatDateTime(ticket.claimedAt)}</p>
                      {ticket.notes && <p><strong>Player Notes:</strong> {ticket.notes}</p>}
                    </div>
                    
                    {/* Update Status Form */}
                    <div className={styles.updateForm}>
                      <h4>Update Status</h4>
                      <div className={styles.formGroup}>
                        <label>ETA:</label>
                        <input
                          type="text"
                          placeholder="e.g., 5 minutes, En route"
                          value={updateFormData[ticket.id]?.eta || ''}
                          onChange={(e) => setUpdateFormData({
                            ...updateFormData,
                            [ticket.id]: { ...updateFormData[ticket.id], eta: e.target.value, notes: updateFormData[ticket.id]?.notes || '' }
                          })}
                          maxLength={100}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Status Notes:</label>
                        <textarea
                          placeholder="e.g., Heading to location, Almost there"
                          value={updateFormData[ticket.id]?.notes || ''}
                          onChange={(e) => setUpdateFormData({
                            ...updateFormData,
                            [ticket.id]: { ...updateFormData[ticket.id], notes: e.target.value, eta: updateFormData[ticket.id]?.eta || '' }
                          })}
                          maxLength={500}
                          rows={2}
                        />
                      </div>
                      <button 
                        className="button"
                        onClick={() => handleUpdateTicket(ticket.id)}
                        disabled={!updateFormData[ticket.id]?.eta && !updateFormData[ticket.id]?.notes}
                      >
                        Update
                      </button>
                    </div>

                    <div className={styles.ticketActions}>
                      <button 
                        className="button"
                        onClick={() => setActiveChatTicketId(ticket.id)}
                        title="Open chat with patient"
                      >
                        💬 Chat {unreadCounts[ticket.id] > 0 && (
                          <span style={{ fontWeight: 'bold', color: '#ff4444', marginLeft: '5px' }}>
                            ({unreadCounts[ticket.id]})
                          </span>
                        )}
                      </button>
                      <button 
                        className="saveButton"
                        onClick={() => handleCompleteTicket(ticket.id)}
                      >
                        Mark Complete
                      </button>
                      <button 
                        className="cancelButton"
                        onClick={() => handleReleaseTicket(ticket.id)}
                      >
                        Release Ticket
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MY TICKETS HISTORY TAB */}
        {activeTab === 'myTickets' && (
          <div className={styles.tabPanel}>
            <h2>My Requests</h2>
            <p className={styles.tabDescription}>SOS requests you have submitted</p>
            
            {/* Filter Controls */}
            <div className={styles.filterControls}>
              <label>Filter by Status:</label>
              <select 
                value={myTicketFilter} 
                onChange={(e) => setMyTicketFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Tickets</option>
                <option value="pending">Pending</option>
                <option value="inprogress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : myTicketHistory.filter(t => myTicketFilter === 'all' || t.status.toLowerCase() === myTicketFilter).length === 0 ? (
              <p className={styles.emptyMessage}>No tickets found.</p>
            ) : (
              <div className={styles.compactTicketsList}>
                {myTicketHistory
                  .filter(t => myTicketFilter === 'all' || t.status.toLowerCase() === myTicketFilter)
                  .map(ticket => (
                  <div key={ticket.id} className={styles.compactTicketItem}>
                    <div 
                      className={styles.compactTicketHeader}
                      onClick={() => setExpandedMyTicket(expandedMyTicket === ticket.id ? null : ticket.id)}
                    >
                      <div className={styles.compactTicketInfo}>
                        <span className={styles.ticketId}>#{ticket.id}</span>
                        <span className={styles.ticketLocation}>{ticket.currentSystem} - {ticket.currentLocation}</span>
                        <span className={styles.ticketDate}>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className={styles.compactTicketStatus}>
                        <span className={`${styles.statusBadge} ${getStatusBadgeClass(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={styles.expandIcon}>
                          {expandedMyTicket === ticket.id ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {expandedMyTicket === ticket.id && (
                      <div className={styles.compactTicketDetails}>
                        <div className={styles.detailGrid}>
                          <div className={styles.detailItem}>
                            <strong>In-Game Username:</strong> {ticket.inGameUsername}
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Death Timer:</strong> 
                            <span>
                              {ticket.deathTimerRemaining} minutes (Original)
                              <br />
                              <span style={{ 
                                color: calculateActualRemainingTime(ticket).isExpired ? '#ef4444' : '#10b981',
                                fontWeight: 'bold' 
                              }}>
                                {calculateActualRemainingTime(ticket).display} (Remaining)
                              </span>
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Created:</strong> {formatDateTime(ticket.createdAt)}
                          </div>
                          {ticket.assignedMedicName && (
                            <div className={styles.detailItem}>
                              <strong>Assigned Medic:</strong> {ticket.assignedMedicName}
                            </div>
                          )}
                          {ticket.claimedAt && (
                            <div className={styles.detailItem}>
                              <strong>Claimed:</strong> {formatDateTime(ticket.claimedAt)}
                            </div>
                          )}
                          {ticket.completedAt && (
                            <div className={styles.detailItem}>
                              <strong>Completed:</strong> {formatDateTime(ticket.completedAt)}
                            </div>
                          )}
                          {ticket.estimatedArrivalTime && (
                            <div className={styles.detailItem}>
                              <strong>ETA:</strong> {ticket.estimatedArrivalTime}
                            </div>
                          )}
                        </div>
                        {ticket.medicNotes && (
                          <div className={styles.notesSection}>
                            <strong>Medic Notes:</strong>
                            <p>{ticket.medicNotes}</p>
                          </div>
                        )}
                        {ticket.notes && (
                          <div className={styles.notesSection}>
                            <strong>Your Notes:</strong>
                            <p>{ticket.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MANAGE ALL TICKETS TAB (Medics/Admins Only) */}
        {activeTab === 'manageAll' && (user?.flags?.includes('AccessMedicalReports') || user?.role === 'Admin') && (
          <div className={styles.tabPanel}>
            <h2>Manage All Tickets</h2>
            <p className={styles.subtitle}>View and manage all user tickets (Admin/Medic view)</p>
            
            {/* Filter Controls */}
            <div className={styles.filterControls}>
              <label>Filter by Status:</label>
              <select 
                value={allTicketFilter} 
                onChange={(e) => setAllTicketFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Tickets</option>
                <option value="pending">Pending</option>
                <option value="inprogress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : allTicketHistory.filter(t => allTicketFilter === 'all' || t.status.toLowerCase() === allTicketFilter).length === 0 ? (
              <p className={styles.emptyMessage}>No tickets found.</p>
            ) : (
              <div className={styles.compactTicketsList}>
                {allTicketHistory
                  .filter(t => allTicketFilter === 'all' || t.status.toLowerCase() === allTicketFilter)
                  .map(ticket => (
                  <div key={ticket.id} className={styles.compactTicketItem}>
                    <div 
                      className={styles.compactTicketHeader}
                      onClick={() => setExpandedAllTicket(expandedAllTicket === ticket.id ? null : ticket.id)}
                    >
                      <div className={styles.compactTicketInfo}>
                        <span className={styles.ticketId}>#{ticket.id}</span>
                        <span className={styles.ticketUser}>{ticket.username}</span>
                        <span className={styles.ticketLocation}>{ticket.currentSystem}</span>
                        <span className={styles.ticketDate}>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className={styles.compactTicketStatus}>
                        <span className={`${styles.statusBadge} ${getStatusBadgeClass(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={styles.expandIcon}>
                          {expandedAllTicket === ticket.id ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {expandedAllTicket === ticket.id && (
                      <div className={styles.compactTicketDetails}>
                        <div className={styles.detailGrid}>
                          <div className={styles.detailItem}>
                            <strong>User:</strong> {ticket.username}
                          </div>
                          <div className={styles.detailItem}>
                            <strong>In-Game Username:</strong> {ticket.inGameUsername}
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Location:</strong> {ticket.currentLocation}
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Death Timer:</strong> 
                            <span>
                              {ticket.deathTimerRemaining} minutes (Original)
                              <br />
                              <span style={{ 
                                color: calculateActualRemainingTime(ticket).isExpired ? '#ef4444' : '#10b981',
                                fontWeight: 'bold' 
                              }}>
                                {calculateActualRemainingTime(ticket).display} (Remaining)
                              </span>
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <strong>Created:</strong> {formatDateTime(ticket.createdAt)}
                          </div>
                          {ticket.assignedMedicName && (
                            <div className={styles.detailItem}>
                              <strong>Assigned Medic:</strong> {ticket.assignedMedicName}
                            </div>
                          )}
                          {ticket.claimedAt && (
                            <div className={styles.detailItem}>
                              <strong>Claimed:</strong> {formatDateTime(ticket.claimedAt)}
                            </div>
                          )}
                          {ticket.completedAt && (
                            <div className={styles.detailItem}>
                              <strong>Completed:</strong> {formatDateTime(ticket.completedAt)}
                            </div>
                          )}
                          {ticket.estimatedArrivalTime && (
                            <div className={styles.detailItem}>
                              <strong>ETA:</strong> {ticket.estimatedArrivalTime}
                            </div>
                          )}
                        </div>
                        {ticket.medicNotes && (
                          <div className={styles.notesSection}>
                            <strong>Medic Notes:</strong>
                            <p>{ticket.medicNotes}</p>
                          </div>
                        )}
                        {ticket.notes && (
                          <div className={styles.notesSection}>
                            <strong>User Notes:</strong>
                            <p>{ticket.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REPORTS TAB (Medics/Admins Only) */}
        {activeTab === 'reports' && (
          <div className={styles.tabPanel}>
            <h2>Medical SOS Reports & Analytics</h2>
            <p className={styles.subtitle}>Performance metrics and ticket statistics</p>

            {/* Date Range Selector */}
            <div className={styles.reportControls}>
              <div className={styles.dateRangeSelector}>
                <label>
                  <span>Start Date:</span>
                  <input
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </label>
                <label>
                  <span>End Date:</span>
                  <input
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </label>
                <button 
                  onClick={fetchReportData}
                  className={styles.refreshButton}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Generate Report'}
                </button>
              </div>
              
              {/* Quick Range Buttons */}
              <div className={styles.quickRanges}>
                <button onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 7);
                  setReportStartDate(start.toISOString().split('T')[0]);
                  setReportEndDate(end.toISOString().split('T')[0]);
                }}>Last 7 Days</button>
                <button onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 30);
                  setReportStartDate(start.toISOString().split('T')[0]);
                  setReportEndDate(end.toISOString().split('T')[0]);
                }}>Last 30 Days</button>
                <button onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 90);
                  setReportStartDate(start.toISOString().split('T')[0]);
                  setReportEndDate(end.toISOString().split('T')[0]);
                }}>Last 90 Days</button>
              </div>
            </div>

            {loading ? (
              <p>Loading report data...</p>
            ) : reportData ? (
              <div className={styles.reportsContainer}>
                {/* Summary Cards */}
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Total Tickets</div>
                    <div className={styles.statValue}>{reportData.totalTickets}</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Completed</div>
                    <div className={styles.statValue}>{reportData.completedTickets}</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Completion Rate</div>
                    <div className={styles.statValue}>{reportData.completionRate.toFixed(1)}%</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Pending</div>
                    <div className={styles.statValue}>{reportData.pendingTickets}</div>
                  </div>
                </div>

                {/* Time Metrics */}
                <div className={styles.metricsSection}>
                  <h3>⏱️ Time Metrics</h3>
                  <div className={styles.metricsGrid}>
                    <div className={styles.metricItem}>
                      <strong>Avg. Time to Claim:</strong>
                      <span>{Math.floor(reportData.averageTimeToClaimMinutes)} minutes</span>
                    </div>
                    <div className={styles.metricItem}>
                      <strong>Avg. Resolution Time:</strong>
                      <span>{Math.floor(reportData.averageResolutionTimeMinutes)} minutes</span>
                    </div>
                    <div className={styles.metricItem}>
                      <strong>Avg. Total Time:</strong>
                      <span>{Math.floor(reportData.averageTimeToCompleteMinutes)} minutes</span>
                    </div>
                  </div>
                </div>

                {/* Status Breakdown */}
                {reportData.statusBreakdown && reportData.statusBreakdown.length > 0 && (
                  <div className={styles.statusSection}>
                    <h3>📊 Status Breakdown</h3>
                    <div className={styles.statusGrid}>
                      {reportData.statusBreakdown.map((item: any) => (
                        <div key={item.Status || item.status} className={styles.statusItem}>
                          <span className={`${styles.statusBadge} ${getStatusBadgeClass((item.Status || item.status || '').toLowerCase())}`}>
                            {item.Status || item.status}
                          </span>
                          <span className={styles.statusCount}>{item.Count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Medic Performance */}
                {reportData.medicPerformance && reportData.medicPerformance.length > 0 && (
                  <div className={styles.medicPerformanceSection}>
                    <h3>🏆 Medic Performance</h3>
                    <div className={styles.performanceTable}>
                      <div className={styles.performanceHeader}>
                        <span>Medic</span>
                        <span>Tickets Completed</span>
                        <span>Avg. Resolution Time</span>
                      </div>
                      {reportData.medicPerformance.map((medic: any) => (
                        <div key={medic.medicId} className={styles.performanceRow}>
                          <span className={styles.medicName}>{medic.medicName}</span>
                          <span>{medic.ticketsCompleted}</span>
                          <span>{Math.floor(medic.avgCompletionTime)} min</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className={styles.emptyMessage}>Select a date range and click "Generate Report" to view statistics.</p>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Chat Modal */}
      {activeChatTicketId && user && (
        <TicketChat
          ticketId={activeChatTicketId}
          currentUserId={user.id}
          onClose={() => {
            setActiveChatTicketId(null);
            // Refresh unread counts after closing chat
            if (myTicket) fetchUnreadCount(myTicket.id);
            if (myAssignments.length > 0) fetchUnreadCounts(myAssignments);
          }}
        />
      )}
    </div>
  );
};

export default MedicalSOS;
